/**
 * Biblio Service
 *
 * Handles biblio source creation using a presigned URL upload flow:
 *   1. Request a presigned S3 PUT URL from the backend
 *   2. Upload the file directly to S3 (bypasses Lambda 6MB limit)
 *   3. Submit source metadata + S3 key to create the biblio record and
 *      transfer the file from S3 to Google Drive
 */
import { FileWithPreview } from "@/hooks/use-file-upload.ts"


// Cached API base URL loaded from aws-exports.json
let BIBLIO_API_URL = ""

/**
 * Load the reports API URL from aws-exports.json configuration.
 *
 * Falls back to a relative /api/reports path for local development
 * when no explicit API URL is configured.
 *
 * @returns The full URL for the reports API endpoint.
 */
async function loadApiUrl(): Promise<string> {
  if (BIBLIO_API_URL) {
    return BIBLIO_API_URL
  }

  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    // VITE_API_URL env var takes precedence (used with npm run dev --host).
    // Falls back to apiUrl from aws-exports.json (CI-generated for deployed envs).
    // Last resort: relative path (will 404 locally but works behind CloudFront).
    const baseUrl = import.meta.env.VITE_API_URL || config.apiUrl || ""
    BIBLIO_API_URL = baseUrl ? `${baseUrl}/api/biblio` : "/api/biblio"
    return BIBLIO_API_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("Reports API URL not configured")
  }
}

/**
 * Response from the presigned URL endpoint.
 */
interface PresignedUrlResponse {
  /** The presigned S3 PUT URL for uploading the file. */
  uploadUrl: string
  /** The S3 object key where the file will be stored. */
  s3Key: string
}

export interface NewReportRequest {
  title: string
  files: FileWithPreview[]
  mapSurveyId?: string
  surveyType?: string
  publicationYear?: string
  authors?: string[]
  institutions?: string[]
  reportNumber?: string
  pages?: string
  city?: string
  createdBy: string
}

/**
 * Response returned by the reports API after successfully creating a
 * biblio source record and transferring the file to Google Drive.
 */
export interface AddReportResponse {
  /** The message from the API (e.g. "Report created successfully"). */
  message: string
  /** The newly allocated numeric source ID in the biblio-sources table. */
  sourceId: number
  /** The generated citation string. */
  citation: string
  /** The Google Drive file ID (present only when a file was uploaded). */
  driveFileId?: string
}

/**
 * A contact for a personal communication source.
 */
export interface BiblioContact {
  /** The contact's name. */
  name: string
}

/**
 * Request payload for creating a personal communication biblio source.
 */
export interface NewPersonalCommunicationRequest {
  /** The title of the personal communication (required). */
  title: string
  /** List of contacts (optional). */
  contacts?: BiblioContact[]
  /** Date of the communication in YYYY-MM-DD format (optional). */
  communicationDate?: string
  /** Release status: PERM_TO_RELEASE, CONFIDENTIAL (optional). */
  usagePermission?: string
  /** File references from presigned URL uploads (optional). */
  files?: { s3Key: string; filename: string }[]
  /** The dataset ID this personal communication is associated with (optional). */
  datasetId?: string
}

/**
 * Response returned by the personal communications API after successfully
 * creating a biblio source record.
 */
export interface AddPersonalCommunicationResponse {
  /** The message from the API. */
  message: string
  /** The newly allocated numeric source ID in the biblio-sources table. */
  sourceId: number
  /** The generated citation string. */
  citation: string
  /** The Google Drive file IDs (present only when files were uploaded). */
  driveFileIds?: string[]
  /** Error details if some file uploads failed (partial success). */
  driveUploadErrors?: string[]
}

/**
 * Request a presigned S3 PUT URL for uploading a file.
 *
 * The returned URL allows the client to upload the file directly to S3,
 * bypassing the Lambda 6MB payload limit.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param filename - The original filename (used for the S3 key suffix).
 * @param contentType - The MIME type of the file being uploaded.
 * @returns The presigned URL and S3 key for the upload.
 * @throws Error if the API call fails or returns a non-OK status.
 */
async function getPresignedUploadUrl(
  idToken: string,
  filename: string,
  contentType: string
): Promise<PresignedUrlResponse> {
  const apiUrl = await loadApiUrl()

  // The presigned-url endpoint is at /api/biblio/presigned-url
  const params = new URLSearchParams({
    filename,
    contentType,
  })

  const response = await fetch(`${apiUrl}/presigned-url?${params.toString()}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to get presigned URL: HTTP ${response.status}`)
  }

  const data: PresignedUrlResponse = await response.json()
  return data
}

/**
 * Upload a file directly to S3 using a presigned PUT URL.
 *
 * This bypasses the Lambda payload limit by sending the file directly
 * to S3 from the browser.
 *
 * @param uploadUrl - The presigned S3 PUT URL.
 * @param file - The File object to upload.
 * @param contentType - The MIME type of the file.
 * @throws Error if the S3 upload fails.
 */
async function uploadFileToS3(
  uploadUrl: string,
  file: File,
  contentType: string
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": contentType,
    },
    body: file,
  })

  if (!response.ok) {
    throw new Error(`S3 upload failed: HTTP ${response.status}`)
  }
}

/**
 * Submit a new report to the biblio-sources system.
 *
 * Uses a presigned URL flow that supports multiple files:
 *   1. For each file, requests a presigned S3 PUT URL
 *   2. Uploads all files directly to S3 in parallel
 *   3. Sends the report metadata + all S3 keys via POST /api/reports
 *
 * The backend Lambda then downloads each file from S3, uploads them to
 * Google Drive, creates the DynamoDB record, and cleans up the S3 objects.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param newReportRequest - The report metadata and file(s) to submit.
 * @returns The API response containing sourceId and optional driveFileId.
 * @throws Error if any step in the upload flow fails.
 */
export async function addReportToBiblio(
  idToken: string,
  newReportRequest: NewReportRequest
): Promise<AddReportResponse> {
  const apiUrl = await loadApiUrl()

  // Filter to only actual File objects (not FileMetadata from initial state)
  const filesToUpload: File[] = newReportRequest.files
    .map(f => f.file)
    .filter((f): f is File => f instanceof File)

  if (filesToUpload.length === 0) {
    throw new Error("At least one file is required to create a report")
  }

  // Step 1 & 2: Get presigned URLs and upload all files to S3 in parallel
  const uploadedFiles: { s3Key: string; filename: string }[] = await Promise.all(
    filesToUpload.map(async (file: File) => {
      const contentType: string = file.type || "application/octet-stream"

      // Get presigned URL for this file
      const { uploadUrl, s3Key } = await getPresignedUploadUrl(
        idToken,
        file.name,
        contentType
      )

      // Upload the file directly to S3
      await uploadFileToS3(uploadUrl, file, contentType)

      return { s3Key, filename: file.name }
    })
  )

  // Step 3: Submit report metadata + all S3 keys to create the record
  const requestBody: Record<string, unknown> = {
    title: newReportRequest.title,
    createdBy: newReportRequest.createdBy,
    files: uploadedFiles,
  }

  // Add optional fields — only include if non-empty
  if (newReportRequest.mapSurveyId) {
    requestBody.mapSurveyId = newReportRequest.mapSurveyId
  }
  if (newReportRequest.surveyType) {
    requestBody.surveyType = newReportRequest.surveyType
  }
  if (newReportRequest.publicationYear) {
    requestBody.publicationYear = newReportRequest.publicationYear
  }
  if (newReportRequest.authors && newReportRequest.authors.length > 0) {
    requestBody.authors = newReportRequest.authors
  }
  if (newReportRequest.institutions && newReportRequest.institutions.length > 0) {
    requestBody.institutions = newReportRequest.institutions
  }
  if (newReportRequest.reportNumber) {
    requestBody.reportNumber = newReportRequest.reportNumber
  }
  if (newReportRequest.pages) {
    requestBody.pages = newReportRequest.pages
  }
  if (newReportRequest.city) {
    requestBody.city = newReportRequest.city
  }

  const response = await fetch(`${apiUrl}/reports`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(requestBody),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const data: AddReportResponse = await response.json()
  return data
}

/**
 * Create a personal communication biblio source record.
 *
 * Optionally supports file uploads using the presigned URL flow:
 *   1. For each file, requests a presigned S3 PUT URL
 *   2. Uploads files directly to S3 in parallel
 *   3. Sends the metadata + S3 keys via POST /api/biblio/personal-communications
 *
 * The backend Lambda creates the DynamoDB record and (if files are provided)
 * downloads each file from S3, uploads them to Google Drive, and cleans up S3.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param request - The personal communication metadata.
 * @param files - Optional file(s) to attach (e.g., source email).
 * @returns The API response containing sourceId and citation.
 * @throws Error if any step fails.
 */
export async function addPersonalCommunication(
  idToken: string,
  request: NewPersonalCommunicationRequest,
  files?: File[]
): Promise<AddPersonalCommunicationResponse> {
  const apiUrl = await loadApiUrl()

  // Upload files to S3 if provided
  let uploadedFiles: { s3Key: string; filename: string }[] = []
  if (files && files.length > 0) {
    uploadedFiles = await Promise.all(
      files.map(async (file: File) => {
        const contentType: string = file.type || "application/octet-stream"

        // Get presigned URL for this file
        const { uploadUrl, s3Key } = await getPresignedUploadUrl(
          idToken,
          file.name,
          contentType
        )

        // Upload the file directly to S3
        await uploadFileToS3(uploadUrl, file, contentType)

        return { s3Key, filename: file.name }
      })
    )
  }

  // Build the request body
  const requestBody: Record<string, unknown> = {
    title: request.title,
  }

  if (request.contacts && request.contacts.length > 0) {
    requestBody.contacts = request.contacts
  }
  if (request.communicationDate) {
    requestBody.communicationDate = request.communicationDate
  }
  if (request.usagePermission) {
    requestBody.usagePermission = request.usagePermission
  }
  if (request.datasetId) {
    requestBody.datasetId = request.datasetId
  }
  if (uploadedFiles.length > 0) {
    requestBody.files = uploadedFiles
  }

  const response = await fetch(`${apiUrl}/personal-communications`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(requestBody),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const data: AddPersonalCommunicationResponse = await response.json()
  return data
}

/**
 * Response returned by the files API after successfully uploading
 * files to Google Drive for an existing source.
 */
export interface AddFilesToSourceResponse {
  /** The source ID the files were added to. */
  sourceId: string
  /** The Google Drive file IDs for the uploaded files. */
  driveFileIds: string[]
  /** The message from the API. */
  message: string
  /** Error details if some uploads failed (partial success). */
  driveUploadErrors?: string[]
}

/**
 * Add files to an existing biblio source.
 *
 * Uses a presigned URL flow that supports multiple files:
 *   1. For each file, requests a presigned S3 PUT URL
 *   2. Uploads all files directly to S3 in parallel
 *   3. Sends the sourceId + all S3 keys via PUT /api/biblio/files
 *
 * The backend Lambda then downloads each file from S3, uploads them to
 * Google Drive with the sourceId as the title and in file metadata,
 * and cleans up the temporary S3 objects.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param sourceId - The ID of the existing source to attach files to.
 * @param files - The file(s) to upload (from the file upload hook).
 * @returns The API response containing driveFileIds.
 * @throws Error if any step in the upload flow fails.
 */
export async function addFilesToSource(
  idToken: string,
  sourceId: string,
  files: FileWithPreview[]
): Promise<AddFilesToSourceResponse> {
  const apiUrl = await loadApiUrl()

  // Filter to only actual File objects (not FileMetadata from initial state)
  const filesToUpload: File[] = files
    .map(f => f.file)
    .filter((f): f is File => f instanceof File)

  if (filesToUpload.length === 0) {
    throw new Error("At least one file is required")
  }

  // Step 1 & 2: Get presigned URLs and upload all files to S3 in parallel
  const uploadedFiles: { s3Key: string; filename: string }[] = await Promise.all(
    filesToUpload.map(async (file: File) => {
      const contentType: string = file.type || "application/octet-stream"

      // Get presigned URL for this file
      const { uploadUrl, s3Key } = await getPresignedUploadUrl(
        idToken,
        file.name,
        contentType
      )

      // Upload the file directly to S3
      await uploadFileToS3(uploadUrl, file, contentType)

      return { s3Key, filename: file.name }
    })
  )

  // Step 3: Submit sourceId + all S3 keys to add files to the source
  const requestBody: Record<string, unknown> = {
    sourceId,
    files: uploadedFiles,
  }

  const response = await fetch(`${apiUrl}/files`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(requestBody),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const data: AddFilesToSourceResponse = await response.json()
  return data
}

/**
 * A single collection record from the biblio-collections table.
 */
export interface BiblioCollection {
  /** The numeric collection identifier. */
  id: number
  /** The display name of the collection. */
  collectionName: string
  collectionType: string
  members: string[]
  sourceIds: string[]
}

/**
 * The `collectionType` value for collections that group PR (parasite rate)
 * literature review articles.
 */
export const PR_LITERATURE_REVIEW_TYPE: string = "PR Literature Review"

/**
 * The `collectionType` value for collections that group routine surveillance
 * (API / parasite incidence) extraction articles.
 */
export const ROUTINE_SURVEILLANCE_TYPE: string = "Routine Surveillance Extraction"

/**
 * The `collectionType` value assigned to user-created collections from the
 * "New Collection" dialog. Chosen to be distinct from the topic-mapped types
 * (PR_LITERATURE_REVIEW_TYPE, ROUTINE_SURVEILLANCE_TYPE) so these collections
 * always fall into the flat "My Collections" group rather than nesting under a
 * topic.
 */
export const USER_COLLECTION_TYPE: string = "User Collection"

/**
 * Maps a collection's `collectionType` to the API topic key that should be
 * applied when viewing that collection's articles, so only articles covering
 * the matching topic are shown. Types not present here impose no topic filter.
 *
 * The topic keys ("pr", "api") match the values accepted by the
 * `GET /api/biblio/journal-articles` `topic` query parameter.
 */
export const COLLECTION_TYPE_TOPIC_KEY_MAP: Partial<Record<string, string>> = {
  [PR_LITERATURE_REVIEW_TYPE]: "pr",
  [ROUTINE_SURVEILLANCE_TYPE]: "api",
}

/**
 * Determine whether a collection is a "public" collection, i.e. one with no
 * access restriction.
 *
 * @param collection - The collection to inspect.
 * @returns True when the collection has no members and is therefore public.
 */
export function isPublicCollection(collection: BiblioCollection): boolean {
  return (collection.members?.length ?? 0) === 0
}

/**
 * Determine whether the given user is an explicit member of a collection.
 * Public collections (empty member list) are NOT considered a membership here;
 * use `isPublicCollection` for that case.
 *
 * @param collection - The collection to inspect.
 * @param userSub - The current user's Auth0 "sub" identifier, or an empty
 *   string / undefined when unknown.
 * @returns True when `userSub` is non-empty and present in the collection's
 *   `members` list.
 */
export function isCollectionMember(
  collection: BiblioCollection,
  userSub: string | undefined
): boolean {
  if (userSub === undefined || userSub.length === 0) {
    return false
  }
  return (collection.members ?? []).includes(userSub)
}

/**
 * Determine whether a collection's pill should be shown to the given user:
 * either the collection is public (empty members) OR the user is an explicit
 * member. Private collections the user does not belong to are hidden.
 *
 * @param collection - The collection to inspect.
 * @param userSub - The current user's Auth0 "sub" identifier, or an empty
 *   string / undefined when unknown.
 * @returns True when the collection is public or the user is a member.
 */
export function isCollectionVisibleToUser(
  collection: BiblioCollection,
  userSub: string | undefined
): boolean {
  return isPublicCollection(collection) || isCollectionMember(collection, userSub)
}

/**
 * Response from the GET /api/biblio/collections endpoint.
 */
export interface BiblioCollectionsResponse {
  /** All collection records from the table. */
  items: BiblioCollection[]
  /** Total number of collections. */
  totalCount: number
}

/**
 * Fetch all biblio collections from the GET /api/biblio/collections endpoint.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @returns Response containing all collections with their full data.
 * @throws Error if the API call fails or returns a non-OK status.
 */
export async function getCollections(
  idToken: string
): Promise<BiblioCollectionsResponse> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/collections`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const data: BiblioCollectionsResponse = await response.json()
  return data
}

/**
 * Request payload for creating a new biblio collection.
 *
 */
export interface CreateCollectionRequest {
  /** The display name for the new collection. */
  collectionName: string
  /**
   * The collection's type. To have the collection appear under "My
   * Collections" (rather than nested beneath a topic), this must NOT equal
   * PR_LITERATURE_REVIEW_TYPE or ROUTINE_SURVEILLANCE_TYPE.
   */
  collectionType: string
  /**
   * Auth0 "sub" identifiers that may access the collection. The backend stores
   * this list verbatim as the collection's access-control "members".
   *
   * Semantics:
   *   - Omitted (undefined): the field is not sent; the collection is public.
   *   - Empty array `[]`: sent explicitly; the collection is still public, but
   *     the request records that "public" was an intentional choice.
   *   - Non-empty: the collection is private to exactly those subs.
   *
   * For a collection private to just its creator, pass the creator's own sub
   * as the single element (e.g. `[currentUserSub]`).
   */
  members?: string[]
}

/**
 * Response from the POST /api/biblio/collections endpoint.
 */
export interface CreateCollectionResponse {
  /** Human-readable success message from the backend. */
  message: string
  /** The created collection record, including its server-assigned id. */
  item: BiblioCollection
}

/**
 * Create a new biblio collection via POST /api/biblio/collections.
 *
 * The backend assigns the numeric id, createdBy (the caller's email from the
 * JWT), and createdDate.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param request - The new collection's name and type.
 * @returns The API response containing the success message and created record.
 * @throws Error if the API call fails or returns a non-OK status. The error
 *   message is the backend-provided error when available, otherwise the HTTP
 *   status.
 */
export async function createCollection(
  idToken: string,
  request: CreateCollectionRequest
): Promise<CreateCollectionResponse> {
  const apiUrl = await loadApiUrl()

  // Build the request body. `members` is forwarded whenever the caller
  // provides it — including an explicit empty array `[]`, which the caller may
  // use to deliberately create a public collection. The field is only omitted
  // when `members` is left off entirely (undefined). Both an omitted field and
  // an empty list result in a public collection on the backend; passing `[]`
  // simply makes that intent explicit in the request.
  const requestBody: Record<string, unknown> = {
    collectionName: request.collectionName,
    collectionType: request.collectionType,
  }
  if (request.members !== undefined) {
    requestBody.members = request.members
  }

  const response = await fetch(`${apiUrl}/collections`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(requestBody),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const data: CreateCollectionResponse = await response.json()
  return data
}

/**
 * Response from the PUT /api/biblio/collections endpoint.
 *
 * The backend returns one of two shapes depending on the outcome:
 *   - A normal update returns `{ message, item }` with the updated record.
 *   - For a user collection, a members update that empties the member list
 *     deletes the collection instead of making it public; in that case the
 *     backend returns `{ message, id, deleted: true }` with no `item`.
 */
export interface UpdateCollectionResponse {
  /** Human-readable success message from the backend. */
  message: string
  /**
   * The updated collection record. Present on a normal update; absent when the
   * update resulted in deletion (see `deleted`).
   */
  item?: BiblioCollection
  /**
   * The affected collection id. Always present when the collection was deleted;
   * may be absent on a normal update (read `item.id` instead).
   */
  id?: number
  /**
   * True when the update emptied a user collection's members and the backend
   * deleted the collection rather than making it public.
   */
  deleted?: boolean
}

/**
 * Update the access-control members of an existing collection via
 * PUT /api/biblio/collections.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param collectionId - The numeric id of the collection to update.
 * @param members - The complete new list of Auth0 "sub" identifiers that may
 *   access the collection. An empty array deletes a user collection.
 * @returns The API response. On a normal update it contains `item`; when the
 *   collection was deleted it contains `deleted: true` and `id`.
 * @throws Error if the API call fails or returns a non-OK status. The error
 *   message is the backend-provided error when available, otherwise the HTTP
 *   status.
 */
export async function updateCollectionMembers({
  idToken,
  collectionId,
  members,
}: {
  idToken: string
  collectionId: number
  members: string[]
}): Promise<UpdateCollectionResponse> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/collections`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      id: collectionId,
      members,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const data: UpdateCollectionResponse = await response.json()
  return data
}

/**
 * Update the display name of an existing collection via
 * PUT /api/biblio/collections.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param collectionId - The numeric id of the collection to rename.
 * @param collectionName - The new, non-empty display name for the collection.
 *   Callers should trim the value before passing it.
 * @returns The API response containing the updated collection record in
 *   `item`. A rename never deletes the collection, so `deleted` is not set.
 * @throws Error if the API call fails or returns a non-OK status (e.g. a 403
 *   when the caller is not a member). The error message is the backend-provided
 *   error when available, otherwise the HTTP status.
 */
export async function updateCollectionName({
  idToken,
  collectionId,
  collectionName,
}: {
  idToken: string
  collectionId: number
  collectionName: string
}): Promise<UpdateCollectionResponse> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/collections`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      id: collectionId,
      collectionName,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const data: UpdateCollectionResponse = await response.json()
  return data
}

/**
 * Response from the DELETE /api/biblio/collections endpoint.
 */
export interface DeleteCollectionResponse {
  /** Human-readable success message from the backend. */
  message: string
  /** The id of the deleted collection. */
  id: number
  /** Always true on a successful delete. */
  deleted: boolean
  /**
   * Source ids that could not be unlinked during deletion (e.g. because the
   * source no longer exists). The collection is still deleted regardless.
   */
  sourceIdsNotUnlinked?: number[]
}

/**
 * Delete a collection via DELETE /api/biblio/collections.
 *
 * Deleting a collection also unlinks it from every source it referenced
 * (removing the collection id from each source's `collectionIds`). Only a
 * member of the collection may delete it; the backend rejects non-members.
 *
 * The collection id is passed as the `id` query-string parameter.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param collectionId - The numeric id of the collection to delete.
 * @returns The API response confirming deletion.
 * @throws Error if the API call fails or returns a non-OK status. The error
 *   message is the backend-provided error when available, otherwise the HTTP
 *   status.
 */
export async function deleteCollection({
  idToken,
  collectionId,
}: {
  idToken: string
  collectionId: number
}): Promise<DeleteCollectionResponse> {
  const apiUrl = await loadApiUrl()

  const params = new URLSearchParams({ id: collectionId.toString() })

  const response = await fetch(`${apiUrl}/collections?${params.toString()}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const data: DeleteCollectionResponse = await response.json()
  return data
}

/**
 * Response from the POST /api/biblio/collections/sources endpoint.
 */
export interface AddSourcesToCollectionResponse {
  /** Human-readable success message from the backend. */
  message: string
  /** The collection id the sources were added to. */
  collectionId: number
  /** The source ids that were successfully linked to the collection. */
  sourceIdsAdded: number[]
  /**
   * Source ids that could not be linked because the source record was not
   * found. Present only when at least one source failed to link.
   */
  sourceIdsNotFound?: number[]
}

/**
 * Add one or more sources (e.g. a journal article) to a collection via
 * POST /api/biblio/collections/sources.
 *
 * The backend creates a bidirectional link: the source ids are appended to the
 * collection's `sourceIds`, and the collection id is appended to each source's
 * `collectionIds`.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param collectionId - The numeric id of the collection to add sources to.
 * @param sourceIds - Non-empty list of numeric source ids to add.
 * @returns The API response describing which source ids were linked.
 * @throws Error if the API call fails or returns a non-OK status. The error
 *   message is the backend-provided error when available, otherwise the HTTP
 *   status.
 */
export async function addSourcesToCollection({
  idToken,
  collectionId,
  sourceIds,
}: {
  idToken: string
  collectionId: number
  sourceIds: number[]
}): Promise<AddSourcesToCollectionResponse> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/collections/sources`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      collectionId,
      sourceIds,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const data: AddSourcesToCollectionResponse = await response.json()
  return data
}

/**
 * Response from the DELETE /api/biblio/collections/sources endpoint.
 */
export interface RemoveSourcesFromCollectionResponse {
  /** Human-readable success message from the backend. */
  message: string
  /** The collection id the sources were removed from. */
  collectionId: number
  /** The source ids that were successfully unlinked from the collection. */
  sourceIdsRemoved: number[]
  /**
   * Source ids whose source record could not be updated because it was not
   * found. Present only when at least one source failed to unlink.
   */
  sourceIdsNotFound?: number[]
}

/**
 * Remove one or more sources (e.g. a journal article) from a collection via
 * DELETE /api/biblio/collections/sources.
 *
 * The backend reverses the bidirectional link: the source ids are removed from
 * the collection's `sourceIds`, and the collection id is removed from each
 * source's `collectionIds`. Source ids not currently linked are ignored, so
 * the call is idempotent.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param collectionId - The numeric id of the collection to remove sources from.
 * @param sourceIds - Non-empty list of numeric source ids to remove.
 * @returns The API response describing which source ids were unlinked.
 * @throws Error if the API call fails or returns a non-OK status. The error
 *   message is the backend-provided error when available, otherwise the HTTP
 *   status.
 */
export async function removeSourcesFromCollection({
  idToken,
  collectionId,
  sourceIds,
}: {
  idToken: string
  collectionId: number
  sourceIds: number[]
}): Promise<RemoveSourcesFromCollectionResponse> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/collections/sources`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      collectionId,
      sourceIds,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const data: RemoveSourcesFromCollectionResponse = await response.json()
  return data
}

/**
 * A file record from Google Drive associated with a biblio source.
 */
export interface GoogleDriveFile {
  /** The Google Drive file ID. */
  id: string
  /** The file name in Google Drive. */
  name: string
  /** The MIME type of the file. */
  mimeType: string
  /** Parent folder IDs. */
  parents?: string[]
  /** URL to view the file in the browser. */
  webViewLink: string
  /** URL for the file thumbnail (may be null). */
  thumbnailLink?: string
  /** URL for the file type icon. */
  iconLink?: string
  /** Custom properties set on the file (e.g. sourceId). */
  properties?: Record<string, string>
  /** The original filename at upload time. */
  originalFilename?: string
  /** MD5 checksum of the file content. */
  md5Checksum?: string
}

/**
 * Response from the GET /api/biblio/files endpoint.
 */
export interface GetSourceFilesResponse {
  /** List of Google Drive file metadata objects for the source. */
  files: GoogleDriveFile[]
  /** The sourceId that was queried. */
  sourceId: string
}

/**
 * Fetch associated Google Drive files for a sourceId from the
 * GET /api/biblio/files?sourceId=... endpoint.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param sourceId - The numeric source ID to retrieve files for.
 * @returns Response containing the list of Google Drive file metadata.
 * @throws Error if the API call fails or returns a non-OK status.
 */
export async function getFilesForSource(
  idToken: string,
  sourceId: number
): Promise<GetSourceFilesResponse> {
  const apiUrl = await loadApiUrl()

  const params = new URLSearchParams({
    sourceId: sourceId.toString(),
  })

  const response = await fetch(`${apiUrl}/files?${params.toString()}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const data: GetSourceFilesResponse = await response.json()
  return data
}

/**
 * Response from the POST /api/biblio/notes endpoint.
 */
export interface AddNoteResponse {
  /** Success message from the API. */
  message: string
  /** The source ID the note was added to. */
  sourceId: number
  /** The note entry that was created. */
  note: {
    noteText: string
    createdBy: string
    createdDate: string
  }
}

/**
 * Add a note to an existing biblio source record.
 *
 * Calls POST /api/biblio/notes with the sourceId, noteText, and createdBy.
 * The backend appends a new entry to the source's 'note' list attribute
 * with the text, the createdBy value, and the current timestamp.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param sourceId - The numeric source ID to add the note to.
 * @param noteText - The text content of the note.
 * @param createdBy - The display name/email of the user creating the note.
 * @returns The API response containing the created note entry.
 * @throws Error if the API call fails or returns a non-OK status.
 */
export async function addNote(
  idToken: string,
  sourceId: number,
  noteText: string,
  createdBy: string
): Promise<AddNoteResponse> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/notes`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      sourceId,
      noteText,
      createdBy,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const data: AddNoteResponse = await response.json()
  return data
}
